import { Link } from "@tanstack/react-router";
import { GraduationCap } from "lucide-react";

import type { TrainingMeStatusItem } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { rememberTrainingReturn } from "@/lib/trainingReturn";

export interface DealCreationBlockedProps {
  /** The current user's per-module training statuses (from /training/me/status). */
  statuses: TrainingMeStatusItem[];
  /** Draft-resume context, when the block interrupted a ?draftId deep-link. */
  draftId?: string;
}

const MODULE_STATUS_LABEL: Record<TrainingMeStatusItem["status"], string> = {
  completed: "Completed",
  in_progress: "In progress",
  not_started: "Not started",
};

/**
 * Shown in place of the deal-registration wizard when a gate-eligible partner
 * has outstanding compulsory training (RACCPE-22). The backend
 * `require_training_complete` dep is the real gate; this explains why and
 * points to the training that lifts it.
 *
 * Deals-gap W2: two differentiated screens. A user who has never started
 * training gets "Complete your Level 1 Certification…" + [Start Training Now]
 * + the mandatory module list at 0%; a partially-complete user gets "Finish
 * your Level 1 Certification" + a % progress bar + per-module status +
 * [Finish Training Now]. Both CTAs persist a return target so finishing
 * training can route back to deal creation (lib/trainingReturn.ts).
 */
export function DealCreationBlocked({ statuses, draftId }: DealCreationBlockedProps) {
  const compulsory = statuses.filter((s) => s.is_compulsory);
  const completed = compulsory.filter((s) => s.status === "completed").length;
  const total = compulsory.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  // "Never started" here is scoped to what the gate cares about: no compulsory
  // module has any progress. (The team report's has_started is the cohort-side
  // equivalent of the same three-state vocabulary.)
  const neverStarted = compulsory.every((s) => s.status === "not_started");

  const remember = () =>
    rememberTrainingReturn({
      to: "/dashboard/partner/deals/new",
      ...(draftId ? { draftId } : {}),
    });

  return (
    <div className="container py-6">
      <Card data-testid="deal-creation-blocked" className="mx-auto max-w-xl">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </div>
          {neverStarted ? (
            <>
              <CardTitle>Complete your Level 1 Certification to register deals</CardTitle>
              <CardDescription>
                New deals can&apos;t be registered until you complete the mandatory training below.
                Your existing deals are unaffected and can still be edited.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle>Finish your Level 1 Certification</CardTitle>
              <CardDescription>
                You&apos;re part-way there — finish the remaining mandatory training below to unlock
                deal registration. Your existing deals are unaffected.
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {!neverStarted && (
            <div data-testid="certification-progress">
              <div className="flex items-center justify-between text-sm">
                <span>
                  {completed} of {total} mandatory modules complete
                </span>
                <span className="font-medium">{percent}%</span>
              </div>
              <div
                className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Level 1 Certification progress"
              >
                <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
              </div>
            </div>
          )}
          {compulsory.length > 0 && (
            <ul className="space-y-1.5 text-sm" data-testid="mandatory-module-list">
              {compulsory.map((m) => (
                <li key={m.module_id} className="flex items-center justify-between gap-3">
                  <span>{m.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {neverStarted ? "0%" : MODULE_STATUS_LABEL[m.status]}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Button asChild>
            <Link to="/dashboard/partner/training" onClick={remember}>
              {neverStarted ? "Start training now" : "Finish training now"}
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
